import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Search, Target, TrendingUp, Rocket, MessageSquare,
  ChevronRight, Users, Brain, Lightbulb, Zap, Award
} from 'lucide-react';

const FiveDShowcase = ({ onViewMethodology }) => {
  const { t } = useTranslation();
  const [activePhase, setActivePhase] = useState(null);

  const phases = [
    {
      id: 'discover',
      icon: Search,
      color: 'from-blue-500 to-blue-600',
      bgLight: 'bg-blue-50',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-500',
      competencies: ['selfAwareness', 'empathy', 'teamDynamics']
    },
    {
      id: 'define',
      icon: Target,
      color: 'from-purple-500 to-purple-600',
      bgLight: 'bg-purple-50',
      textColor: 'text-purple-600',
      borderColor: 'border-purple-500',
      competencies: ['strategicGoals', 'clearCommunication', 'goalOwnership']
    },
    {
      id: 'develop',
      icon: TrendingUp,
      color: 'from-green-500 to-green-600',
      bgLight: 'bg-green-50',
      textColor: 'text-green-600',
      borderColor: 'border-green-500',
      competencies: ['coachingSkills', 'creativity', 'continuousLearning']
    },
    {
      id: 'drive',
      icon: Rocket,
      color: 'from-orange-500 to-orange-600',
      bgLight: 'bg-orange-50',
      textColor: 'text-orange-600',
      borderColor: 'border-orange-500',
      competencies: ['actionOrientation', 'followUp', 'motivation']
    },
    {
      id: 'debrief',
      icon: MessageSquare,
      color: 'from-teal-500 to-teal-600',
      bgLight: 'bg-teal-50',
      textColor: 'text-teal-600',
      borderColor: 'border-teal-500',
      competencies: ['feedback', 'selfAssessment', 'learningFromProcess']
    }
  ];

  return (
    <section id="methodology" className="py-20 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Award className="w-4 h-4" />
            {t('fiveD.badge')}
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {t('fiveD.title')}
          </h2>
          <p className="text-lg text-gray-600">
            {t('fiveD.subtitle')}
          </p>
        </div>

        {/* 5D Visual - Desktop */}
        <div className="hidden lg:block relative max-w-5xl mx-auto mb-16">
          {/* Connecting Line */}
          <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 via-green-500 via-orange-500 to-teal-500 transform -translate-y-1/2 rounded-full opacity-30" />
          
          {/* Phases */}
          <div className="relative flex justify-between items-center">
            {phases.map((phase, index) => {
              const Icon = phase.icon;
              const isActive = activePhase === phase.id;
              
              return (
                <div 
                  key={phase.id}
                  className="relative group"
                  onMouseEnter={() => setActivePhase(phase.id)}
                  onMouseLeave={() => setActivePhase(null)}
                >
                  {/* Phase Circle */}
                  <div 
                    className={`
                      relative z-10 w-24 h-24 rounded-full bg-gradient-to-br ${phase.color} 
                      flex items-center justify-center cursor-pointer
                      transform transition-all duration-300 shadow-lg
                      ${isActive ? 'scale-110 shadow-2xl' : 'hover:scale-105'}
                    `}
                  >
                    <Icon className="w-10 h-10 text-white" />
                    
                    {/* Phase Number */}
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md">
                      <span className={`text-sm font-bold ${phase.textColor}`}>{index + 1}</span>
                    </div>
                  </div>
                  
                  {/* Phase Label */}
                  <div className="mt-4 text-center">
                    <h3 className={`font-bold text-lg ${isActive ? phase.textColor : 'text-gray-900'} transition-colors`}>
                      {t(`fiveD.phases.${phase.id}.title`)}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {t(`fiveD.phases.${phase.id}.subtitle`)}
                    </p>
                  </div>

                  {/* Expanded Card on Hover */}
                  {isActive && (
                    <div 
                      className={`
                        absolute top-full left-1/2 transform -translate-x-1/2 mt-6 z-20
                        w-72 p-5 bg-white rounded-xl shadow-2xl border-t-4 ${phase.borderColor}
                        animate-fadeIn
                      `}
                    >
                      <p className="text-gray-600 text-sm mb-4">
                        {t(`fiveD.phases.${phase.id}.description`)}
                      </p>
                      <div className="space-y-2">
                        {phase.competencies.map((comp) => (
                          <div key={comp} className={`flex items-center gap-2 ${phase.bgLight} px-3 py-2 rounded-lg`}>
                            <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${phase.color}`} />
                            <span className="text-sm font-medium text-gray-700">
                              {t(`fiveD.competencies.${comp}`)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* 5D Visual - Mobile */}
        <div className="lg:hidden space-y-4 mb-12">
          {phases.map((phase, index) => {
            const Icon = phase.icon;
            const isActive = activePhase === phase.id;
            
            return (
              <div 
                key={phase.id}
                className={`
                  p-5 rounded-xl border-2 transition-all duration-300 cursor-pointer
                  ${isActive ? `${phase.bgLight} ${phase.borderColor}` : 'bg-white border-gray-200'}
                `}
                onClick={() => setActivePhase(isActive ? null : phase.id)}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${phase.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold ${phase.textColor}`}>0{index + 1}</span>
                      <h3 className="font-bold text-gray-900">{t(`fiveD.phases.${phase.id}.title`)}</h3>
                    </div>
                    <p className="text-sm text-gray-500">{t(`fiveD.phases.${phase.id}.subtitle`)}</p>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${isActive ? 'rotate-90' : ''}`} />
                </div>
                
                {isActive && (
                  <div className="mt-4 pt-4 border-t border-gray-200 animate-fadeIn">
                    <p className="text-gray-600 text-sm mb-3">
                      {t(`fiveD.phases.${phase.id}.description`)}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {phase.competencies.map((comp) => (
                        <span key={comp} className={`${phase.bgLight} ${phase.textColor} px-3 py-1 rounded-full text-xs font-medium`}>
                          {t(`fiveD.competencies.${comp}`)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12">
          <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="text-3xl font-bold text-blue-600 mb-1">15</div>
            <div className="text-sm text-gray-600">{t('fiveD.stats.competencies')}</div>
          </div>
          <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="text-3xl font-bold text-purple-600 mb-1">5</div>
            <div className="text-sm text-gray-600">{t('fiveD.stats.phases')}</div>
          </div>
          <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="text-3xl font-bold text-green-600 mb-1">3</div>
            <div className="text-sm text-gray-600">{t('fiveD.stats.dimensions')}</div>
          </div>
          <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="text-3xl font-bold text-orange-600 mb-1">100%</div>
            <div className="text-sm text-gray-600">{t('fiveD.stats.evidenceBased')}</div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button 
            onClick={onViewMethodology}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-full font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            data-testid="view-methodology-btn"
          >
            {t('fiveD.cta')}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

export default FiveDShowcase;
